import { ButtonHTMLAttributes, memo, ReactNode } from "react";
import styled from "@emotion/styled";
import theme from "../../theme";
import { DotLoader } from "../dotLoader";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  bgColor: string;
  hoverColor: string;
  width?: string;
  height?: string;
  color?: string;
  isDisabled?: boolean;
  onClick?: any;
  children: ReactNode;
  paddingX?: string;
  paddingT?: string;
  paddingB?: string;
  isLoading?: boolean;
}

const { md } = theme.screen;

export const Button = memo(
  ({
    width,
    height,
    bgColor,
    color,
    isDisabled,
    hoverColor,
    onClick,
    children,
    paddingX = "10px",
    paddingT = "6.5px",
    paddingB = "7.5px",
    isLoading,
    ...buttonProps
  }: Props) => {
    return (
      <CustomButton
        width={width}
        height={height}
        onClick={onClick}
        bgColor={bgColor}
        color={color}
        hoverColor={hoverColor}
        isDisabled={isDisabled || isLoading}
        paddingX={paddingX}
        paddingT={paddingT}
        paddingB={paddingB}
        {...buttonProps}
        disabled={isDisabled || isLoading}
      >
        {isLoading ? <DotLoader color={theme.color.white} /> : children}
      </CustomButton>
    );
  }
);

const CustomButton = styled.button<{
  width?: string;
  height?: string;
  bgColor: string;
  color?: string;
  isDisabled?: boolean;
  hoverColor: string;
  paddingX: string;
  paddingT: string;
  paddingB: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: ${({ width }) => (width ? width : "auto")};
  height: ${({ height }) => (height ? height : "50px")};
  background: ${({ isDisabled, bgColor }) =>
    isDisabled ? `${theme.statusColor.DISABLED}` : bgColor};
  border-radius: 3px;
  color: ${({ color }) => (color ? color : theme.color.white)};
  border: none;
  font-weight: 700;
  font-size: 15px;
  padding-right: ${({ paddingX }) => paddingX};
  padding-left: ${({ paddingX }) => paddingX};
  padding-top: ${({ paddingT }) => paddingT};
  padding-bottom: ${({ paddingB }) => paddingB};
  cursor: ${({ isDisabled }) => (isDisabled ? "default" : "pointer")};

  @media (max-width: ${md}px) {
    height: ${({ height }) => (height ? height : "30px")};
    font-size: ${theme.fontSize.FONT_2};
  }

  &:hover {
    background-color: ${({ isDisabled, hoverColor }) =>
      isDisabled ? `${theme.statusColor.DISABLED}` : hoverColor};
  }

  :disabled {
    background-color: ${theme.statusColor.DISABLED};
    cursor: default;
  }
`;
